import React, { useState, useEffect, useRef } from 'react';
import { getFbFormMappings, createFbFormMapping, deleteFbFormMapping, getAdminSchools } from '@/actions/reportsAdmin';
import { Search, Facebook, Trash2, Plus, X } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';

interface FbFormMapping {
  map_id: number;
  form_id: string;
  form_name: string;
  client_id: number;
  client_name: string;
  slate_name: string;
  slate_id: number;
  cityName: string;
  areaName: string;
}

interface SchoolOption {
  slate_id: number;
  slate_name: string;
  cityName: string;
  areaName: string;
  client_id: number;
}

const FbFormMappingsAdmin: React.FC = () => {
  const [mappings, setMappings] = useState<FbFormMapping[]>([]);
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [formId, setFormId] = useState('');
  const [selectedSchool, setSelectedSchool] = useState<SchoolOption | null>(null);
  const [schoolSearchQuery, setSchoolSearchQuery] = useState('');
  const [showSchoolSuggestions, setShowSchoolSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMappings();
    fetchSchools();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSchoolSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchMappings = async () => {
    try {
      setIsLoading(true);
      const response = await getFbFormMappings();
      if (response.success) {
        setMappings((response.data as FbFormMapping[]) || []);
      } else {
        showToast('Failed to fetch mappings', 'error');
      }
    } catch (error) {
      console.error('Error fetching mappings:', error);
      showToast('Failed to fetch mappings', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSchools = async () => {
    try {
      const response = await getAdminSchools();
      if (response.success) {
        setSchools((response.data as SchoolOption[]) || []);
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
    }
  };

  const handleAddMapping = async () => {
    if (!formId.trim()) {
      showToast('Please enter a Form ID', 'error');
      return;
    }
    if (!selectedSchool) {
      showToast('Please select a school', 'error');
      return;
    }

    if (!selectedSchool.client_id) {
      showToast('Could not find client ID for this school', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await createFbFormMapping({
        form_id: formId.trim(),
        client_id: selectedSchool.client_id,
      });
      if (response.success) {
        showToast('Mapping created successfully', 'success');
        setFormId('');
        setSelectedSchool(null);
        setSchoolSearchQuery('');
        setShowAddForm(false);
        fetchMappings();
      } else {
        showToast(response.message || 'Failed to create mapping', 'error');
      }
    } catch (error) {
      console.error('Error creating mapping:', error);
      showToast('Failed to create mapping', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMapping = async (mapId: number) => {
    if (!confirm('Are you sure you want to delete this mapping?')) {
      return;
    }

    try {
      const response = await deleteFbFormMapping(mapId);
      if (response.success) {
        showToast('Mapping deleted successfully', 'success');
        fetchMappings();
      } else {
        showToast('Failed to delete mapping', 'error');
      }
    } catch (error) {
      console.error('Error deleting mapping:', error);
      showToast('Failed to delete mapping', 'error');
    }
  };

  const filteredMappings = mappings.filter(m =>
    m.form_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.form_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.slate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSchools = schools.filter(school =>
    school.slate_name.toLowerCase().includes(schoolSearchQuery.toLowerCase()) ||
    (school.cityName && school.cityName.toLowerCase().includes(schoolSearchQuery.toLowerCase())) ||
    (school.areaName && school.areaName.toLowerCase().includes(schoolSearchQuery.toLowerCase()))
  );

  const handleSelectSchool = (school: SchoolOption) => {
    setSelectedSchool(school);
    setSchoolSearchQuery(school.slate_name);
    setShowSchoolSuggestions(false);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <div className="flex items-center gap-4">
          {/* Left Side - Icon, Title with Badge */}
          <div className="flex items-center gap-3 flex-1">
            <div className="flex-shrink-0">
              <div className="p-2 bg-brandColor rounded-lg text-white">
                <Facebook className="h-5 w-5" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-semibold text-gray-900">FB Form Mappings</h1>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                  {mappings.length}
                </span>
              </div>
              <p className="text-xs text-gray-500">Map Facebook lead forms to schools</p>
            </div>
          </div>

          {/* Right Side - Search and Add Button */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search mappings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brandColor focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brandColor text-white text-sm font-medium rounded-md hover:bg-brandColorDark"
            >
              <Plus size={16} />
              <span>Add Mapping</span>
            </button>
          </div>
        </div>
      </div>

      {/* Add Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 bg-brandColor text-white">
              <h2 className="text-lg font-semibold">Add New FB Form Mapping</h2>
              <button onClick={() => setShowAddForm(false)} className="text-white hover:text-gray-200">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* School Selector */}
              <div ref={searchRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
                <input
                  type="text"
                  value={schoolSearchQuery}
                  onChange={(e) => {
                    setSchoolSearchQuery(e.target.value);
                    setShowSchoolSuggestions(true);
                    if (!e.target.value) setSelectedSchool(null);
                  }}
                  onFocus={() => setShowSchoolSuggestions(true)}
                  placeholder="Search and select a school..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brandColor focus:border-transparent"
                />
                {showSchoolSuggestions && schoolSearchQuery && filteredSchools.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
                    {filteredSchools.slice(0, 10).map((school) => (
                      <div
                        key={school.slate_id}
                        onClick={() => handleSelectSchool(school)}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      >
                        <div className="font-medium text-gray-900">{school.slate_name}</div>
                        {(school.areaName || school.cityName) && (
                          <div className="text-xs text-gray-500">
                            {[school.areaName, school.cityName].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Form ID Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Facebook Form ID</label>
                <input
                  type="text"
                  value={formId}
                  onChange={(e) => setFormId(e.target.value)}
                  placeholder="Enter Facebook form ID..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brandColor focus:border-transparent"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddMapping} disabled={isSubmitting}>
                  {isSubmitting ? 'Adding...' : 'Add Mapping'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto px-4 mt-4">
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-brandColor text-white sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Form ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Form Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">School</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">City</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Area</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    Loading mappings...
                  </td>
                </tr>
              ) : filteredMappings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No mappings found
                  </td>
                </tr>
              ) : (
                filteredMappings.map((mapping, index) => (
                  <tr
                    key={mapping.map_id}
                    className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                  >
                    <td className="px-4 py-2 text-sm text-gray-600">{mapping.map_id}</td>
                    <td className="px-4 py-2 text-sm">
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs">{mapping.form_id}</code>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {mapping.form_name || '-'}
                    </td>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">
                      {mapping.slate_name || '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {mapping.cityName || '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {mapping.areaName || '-'}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => handleDeleteMapping(mapping.map_id)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Delete mapping"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t bg-gray-50">
        <div className="text-sm text-gray-600">
          Showing {filteredMappings.length} of {mappings.length} mappings
        </div>
      </div>
    </div>
  );
};

export default FbFormMappingsAdmin;
